import { useState } from 'react'
import {
  Box,
  Typography,
  MenuItem,
  FormControl,
  Select,
  Dialog,
  Button,
  TextField,
  Divider,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles(theme => ({
  timeframeContainer: {
    display: 'flex',
    alignItems: 'center',
    float: 'right',
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(4),
    marginTop: theme.spacing(2),
  },
  timeframeLabel: {
    marginRight: theme.spacing(1),
  },
  timeframeSelect: { width: 200, marginRight: 10, zIndex: 1 },
}))

const DateRangeSelector = ({ onChange }) => {
  const classes = useStyles()
  const [timeframe, setTimeframe] = useState('1h')
  const [isCustomRange, setIsCustomRange] = useState(false)
  const [open, setOpen] = useState(false)
  const [customRange, setCustomRange] = useState({
    startDate: null,
    endDate: null,
  })
  const [displayValue, setDisplayValue] = useState('1 hour')

  const handleTimeframeSelect = (value, label) => {
    setTimeframe(value)
    setIsCustomRange(false)
    setDisplayValue(label)
    setOpen(false)

    let startTime = '-7d'
    const endTime = null

    switch (value) {
      case '5m':
        startTime = '-5m'
        break
      case '30m':
        startTime = '-30m'
        break
      case '1h':
        startTime = '-1h'
        break
      case '3h':
        startTime = '-3h'
        break
      case '12h':
        startTime = '-12h'
        break
      case '24h':
        startTime = '-24h'
        break
      case '7d':
        startTime = '-7d'
        break
      case '60d':
        startTime = '-60d'
        break
      default:
        startTime = '-7d'
    }

    onChange(startTime, endTime)
  }

  const handleCustomRangeChange = (field, value) => {
    setCustomRange({ ...customRange, [field]: value })
  }

  const applyCustomRange = () => {
    if (customRange.startDate && customRange.endDate) {
      setIsCustomRange(true)
      setTimeframe('custom')

      const startFormatted = formatDateForDisplay(customRange.startDate)
      const endFormatted = formatDateForDisplay(customRange.endDate)
      setDisplayValue(`${startFormatted} - ${endFormatted}`)

      onChange(customRange.startDate, customRange.endDate)
      setOpen(false)
    }
  }

  const formatDateForDisplay = date => {
    if (!date) return ''
    return new Date(date).toLocaleDateString()
  }

  return (
    <div className={classes.timeframeContainer}>
      <Typography variant="body2" className={classes.timeframeLabel}>
        Timeframe:
      </Typography>
      <FormControl
        variant="outlined"
        size="small"
        className={classes.timeframeSelect}
      >
        <Select
          value={timeframe}
          renderValue={() => displayValue}
          onClick={() => setOpen(true)}
          open={false}
        >
          <MenuItem value={timeframe}>{displayValue}</MenuItem>
        </Select>
      </FormControl>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <Box p={2}>
          <Typography variant="h6" style={{ marginBottom: 20 }}>
            Select Timeframe
          </Typography>

          <Box mb={2}>
            <Typography variant="subtitle2" style={{ marginBottom: 15 }}>
              Preset Ranges
            </Typography>
            <Box display="flex" flexWrap="wrap">
              {[
                { value: '5m', label: '5 minutes' },
                { value: '30m', label: '30 minutes' },
                { value: '1h', label: '1 hour' },
                { value: '3h', label: '3 hours' },
                { value: '12h', label: '12 hours' },
                { value: '24h', label: '24 hours' },
                { value: '7d', label: '7 days' },
                { value: '60d', label: '60 days' },
              ].map(option => (
                <Box key={option.value} mr={1} mb={1}>
                  <Button
                    variant={
                      timeframe === option.value ? 'contained' : 'outlined'
                    }
                    size="small"
                    color={timeframe === option.value ? 'primary' : 'default'}
                    onClick={() =>
                      handleTimeframeSelect(option.value, option.label)
                    }
                  >
                    {option.label}
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>

          <Divider />

          <Box mt={2}>
            <Typography variant="subtitle2" style={{ marginBottom: 15 }}>
              Custom Range
            </Typography>
            <Box display="flex" alignItems="center" mb={1}>
              <Box width="50%" pr={1}>
                <TextField
                  label="From"
                  type="date"
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={customRange.startDate || ''}
                  onChange={e =>
                    handleCustomRangeChange('startDate', e.target.value)
                  }
                />
              </Box>
              <Box width="50%" pl={1}>
                <TextField
                  label="To"
                  type="date"
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={customRange.endDate || ''}
                  onChange={e =>
                    handleCustomRangeChange('endDate', e.target.value)
                  }
                />
              </Box>
            </Box>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              disabled={!customRange.startDate || !customRange.endDate}
              onClick={applyCustomRange}
            >
              Apply Custom Range
            </Button>
          </Box>
        </Box>
      </Dialog>
    </div>
  )
}

export default DateRangeSelector
